import React from 'react';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import { GraphDevice, STACKLIGHT_STRIP_COUNT, stacklightStripRefs, ws2812Devices } from '../flow/graph';
import { useI18n } from '../i18n';

type StripRefsFieldProps = {
  device: GraphDevice;
  existing: GraphDevice[];
  disabled?: boolean;
  onChange: (strips: string[]) => void;
};

const StripRefsField: React.FC<StripRefsFieldProps> = ({
  device,
  existing,
  disabled,
  onChange,
}) => {
  const { t } = useI18n();
  const strips = ws2812Devices(existing.filter((item) => item.id !== device.id));
  const rows = stacklightStripRefs(device);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {strips.length < STACKLIGHT_STRIP_COUNT ? (
        <Alert severity="warning">
          {t('deviceConfig.needStrips')}
          {' '}
          {t('deviceConfig.needStripsCount', { count: strips.length })}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">{t('deviceConfig.needStrips')}</Typography>
      )}
      {rows.map((row, index) => {
        const taken = new Set(rows.filter((_, other) => other !== index && rows[other]));
        return (
          <TextField
            key={index}
            select
            size="small"
            fullWidth
            label={t('deviceConfig.stripRef', { index: index + 1 })}
            value={row}
            disabled={disabled || strips.length === 0}
            onChange={(e) => {
              const next = [...rows];
              next[index] = e.target.value;
              onChange(next);
            }}
          >
            <MenuItem value="">{t('deviceConfig.stripPick')}</MenuItem>
            {strips.map((strip) => (
              <MenuItem key={strip.id} value={strip.id} disabled={taken.has(strip.id)}>
                {strip.id}
              </MenuItem>
            ))}
          </TextField>
        );
      })}
    </Box>
  );
};

export default StripRefsField;
